import { getAuth } from 'firebase/auth';
import { getFirebaseApp } from '../lib/firebase';
import type { CustomerReliability } from '../lib/reliability';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const firebaseKey = import.meta.env.VITE_FIREBASE_API_KEY || '';
const IS_DEV = !firebaseKey || firebaseKey.startsWith('your_');

async function getAuthToken(): Promise<string | null> {
  // In dev mode, send a dev token derived from the session user
  if (IS_DEV) {
    const saved = sessionStorage.getItem('dev-auth-user');
    if (!saved) return null;
    return `dev-token-${btoa(saved)}`;
  }
  try {
    const app = getFirebaseApp();
    if (!app) return null;
    const auth = getAuth(app);
    const user = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch {
    return null;
  }
}

/**
 * Authenticated fetch that returns the raw body as a Blob — for file
 * downloads (CSV exports). Same auth + error handling as fetchApi; the
 * filename comes from Content-Disposition when the server exposes it.
 */
async function fetchBlob(endpoint: string): Promise<{ blob: Blob; filename: string | null }> {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: await response.blob(), filename: match ? match[1] : null };
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const hasJsonBody = options?.body !== undefined && !(options.body instanceof FormData);
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  return response.json();
}

export const api = {
  // Health check (admin operational dashboard)
  getHealthCheck: () => fetchApi<any>('/admin/health-check'),

  // Locations
  // The Locations page is the single switch: a location that is switched
  // off disappears from every other screen. Operational screens therefore
  // use getLocations() (enabled only); only the Locations page itself uses
  // getAllLocations(), so a disabled location can be switched back on.
  getLocations: () => fetchApi<any[]>('/locations'),
  getAllLocations: () => fetchApi<any[]>('/locations?includeInactive=true'),
  setLocationOnlineBookings: (id: number, accepting: boolean) =>
    fetchApi<any>(`/locations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ accepting_online_bookings: accepting }),
    }),
  /** Future BOOKED appointments at a location as a CSV file (admin only). */
  exportLocationBookings: (locationId: number) =>
    fetchBlob(`/bookings/export.csv?locationId=${locationId}`),
  createLocation: (data: any) =>
    fetchApi<any>('/locations', { method: 'POST', body: JSON.stringify(data) }),
  updateLocation: (id: number, data: any) =>
    fetchApi<any>(`/locations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  getOpeningHours: (locationId?: number) =>
    fetchApi<any[]>(locationId ? `/opening-hours?locationId=${locationId}` : '/opening-hours'),
  replaceOpeningHours: (locationId: number, hours: any[]) =>
    fetchApi<any[]>(`/locations/${locationId}/opening-hours`, {
      method: 'PUT',
      body: JSON.stringify({ hours }),
    }),

  // Services
  getServices: () => fetchApi<any[]>('/services'),
  createService: (data: any) =>
    fetchApi<any>('/services', { method: 'POST', body: JSON.stringify(data) }),
  updateService: (id: number, data: any) =>
    fetchApi<any>(`/services/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteService: (id: number) => fetchApi<any>(`/services/${id}`, { method: 'DELETE' }),
  syncServicesWithSquare: () => fetchApi<any>('/services/sync-square', { method: 'POST' }),

  // Barbers
  getBarbers: (locationId?: number, opts?: { includeInactive?: boolean }) => {
    const params = new URLSearchParams();
    if (locationId) params.set('locationId', String(locationId));
    if (opts?.includeInactive) params.set('includeInactive', 'true');
    const qs = params.toString();
    return fetchApi<any[]>(qs ? `/barbers?${qs}` : '/barbers');
  },
  getBarber: (id: number) => fetchApi<any>(`/barbers/${id}`),
  getBarberServices: (id: number) => fetchApi<any[]>(`/barbers/${id}/services`),
  createBarber: (data: any) =>
    fetchApi<any>('/barbers', { method: 'POST', body: JSON.stringify(data) }),
  updateBarber: (id: number, data: any) =>
    fetchApi<any>(`/barbers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteBarber: (id: number) => fetchApi<any>(`/barbers/${id}`, { method: 'DELETE' }),
  setBarberOrderForLocation: (locationId: number, barberIds: number[]) =>
    fetchApi<any>(`/locations/${locationId}/barber-order`, {
      method: 'PUT',
      body: JSON.stringify({ barber_ids: barberIds }),
    }),
  getBarberDeactivationImpact: (id: number) =>
    fetchApi<{
      barberId: number;
      barberName: string;
      shiftsToRemove: number;
      breaksToRemove: number;
      affectedBookings: {
        bookingId: string;
        startTimeIso: string;
        brisbaneDate: string;
        brisbaneTime: string;
        brisbaneDay: string;
        customerName: string | null;
        customerPhone: string | null;
        locationName: string;
        serviceNames: string[];
        status: string;
      }[];
    }>(`/barbers/${id}/deactivation-impact`),
  syncBarbersWithSquare: () => fetchApi<any>('/barbers/sync-square', { method: 'POST' }),
  assignBarberService: (barberId: number, serviceId: number) =>
    fetchApi<any>(`/barbers/${barberId}/services/${serviceId}`, { method: 'POST' }),
  removeBarberService: (barberId: number, serviceId: number) =>
    fetchApi<any>(`/barbers/${barberId}/services/${serviceId}`, { method: 'DELETE' }),

  // Bookings
  getBookings: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any[]>(`/bookings?${query}`);
  },
  getBooking: (id: number) => fetchApi<any>(`/bookings/${id}`),
  createBooking: (data: any) =>
    fetchApi<any>('/bookings', { method: 'POST', body: JSON.stringify(data) }),
  updateBooking: (id: number, data: any) =>
    fetchApi<any>(`/bookings/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  cancelBooking: (id: number) => fetchApi<any>(`/bookings/${id}`, { method: 'DELETE' }),
  removeBooking: (id: number) => fetchApi<any>(`/bookings/${id}/remove`, { method: 'DELETE' }),
  completeBooking: (id: number, data: any) =>
    fetchApi<any>(`/bookings/${id}/complete`, { method: 'POST', body: JSON.stringify(data) }),
  revertBookingCompletion: (id: number | string) =>
    fetchApi<{ booking: any; transitioned: boolean; warning: string | null }>(
      `/bookings/${id}/revert-completion`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  markNoShow: (id: number) =>
    fetchApi<any>(`/bookings/${id}/no-show`, { method: 'POST', body: JSON.stringify({}) }),

  // Availability
  getAvailability: (locationId: number, barberId: number, date: string, duration?: number) =>
    fetchApi<{ slots: { start: string; end: string }[] }>(
      `/availability?locationId=${locationId}&barberId=${barberId}&date=${date}` +
        (duration ? `&duration=${duration}` : ''),
    ),

  // Rebook at the till — after payment (backend rebook.ts)
  getRebookSuggestion: (bookingId: string) =>
    fetchApi<{
      suggestedDate: string;
      gapDays: number;
      basis: 'usual' | 'new';
      barberId: number;
      serviceIds: number[];
      durationMinutes: number;
    }>(`/bookings/${bookingId}/rebook-suggestion`),
  rebook: (
    bookingId: string,
    data: { startTime: string; barberId: number; customerPhone?: string },
  ) =>
    fetchApi<any>(`/bookings/${bookingId}/rebook`, { method: 'POST', body: JSON.stringify(data) }),
  rebookSkip: (bookingId: string) =>
    fetchApi<{ success: boolean }>(`/bookings/${bookingId}/rebook-skip`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  // Rosters
  getRosters: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any[]>(`/rosters?${query}`);
  },
  createRoster: (data: any) =>
    fetchApi<any>('/rosters', { method: 'POST', body: JSON.stringify(data) }),
  updateRoster: (id: number, data: any) =>
    fetchApi<any>(`/rosters/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteRoster: (id: number) => fetchApi<any>(`/rosters/${id}`, { method: 'DELETE' }),
  copyRosterWeek: (data: any) =>
    fetchApi<any>('/rosters/copy-week', { method: 'POST', body: JSON.stringify(data) }),
  getRosterCoverage: () => fetchApi<any>('/rosters/coverage'),
  autoExtendRosters: () => fetchApi<any>('/rosters/auto-extend', { method: 'POST' }),

  // Breaks
  getBreaks: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any[]>(`/breaks?${query}`);
  },
  createBreak: (data: any) =>
    fetchApi<any>('/breaks', { method: 'POST', body: JSON.stringify(data) }),
  updateBreak: (id: number, data: any) =>
    fetchApi<any>(`/breaks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteBreak: (id: number) => fetchApi<any>(`/breaks/${id}`, { method: 'DELETE' }),

  // Square Terminal
  getSquareDevices: (locationId?: number) =>
    fetchApi<{ devices: any[] }>(`/square/devices${locationId ? `?locationId=${locationId}` : ''}`),
  getSquareLocations: () => fetchApi<{ locations: any[] }>('/square/locations'),
  getCatalogItems: () => fetchApi<{ items: any[] }>('/square/catalog-items'),
  createDeviceCode: (locationId: number, name?: string) =>
    fetchApi<{ id: string; code: string; status: string }>('/square/device-codes', {
      method: 'POST',
      body: JSON.stringify({ locationId, name }),
    }),
  getDeviceCodeStatus: (deviceCodeId: string) =>
    fetchApi<{ id: string; code: string; status: string; deviceId?: string }>(
      `/square/device-codes/${deviceCodeId}`,
    ),
  initiateTerminalCheckout: (
    bookingId: string,
    deviceId: string,
    checkoutItems?: { id: string; variationId: string; name: string; price: number; qty: number }[],
    allowTip?: boolean,
    discount?: { type: 'loyalty' | 'voucher'; bookingServiceId?: number } | null,
  ) =>
    fetchApi<{ checkoutId: string; status: string }>(`/bookings/${bookingId}/checkout`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, checkoutItems, allowTip, discount }),
    }),
  getTerminalCheckoutStatus: (checkoutId: string) =>
    fetchApi<{ status: string; paymentIds: string[] }>(`/square/terminal-checkout/${checkoutId}`),
  cancelTerminalCheckout: (checkoutId: string) =>
    fetchApi<{ success: boolean }>(`/square/terminal-checkout/${checkoutId}`, { method: 'DELETE' }),
  combinedCheckout: (bookingIds: string[], deviceId: string, allowTip?: boolean) =>
    fetchApi<{ checkoutId: string; status: string }>('/bookings/combined-checkout', {
      method: 'POST',
      body: JSON.stringify({ bookingIds, deviceId, allowTip }),
    }),
  combinedComplete: (bookingIds: string[], paymentMethod: string, squarePaymentId?: string) =>
    fetchApi<any[]>('/bookings/combined-complete', {
      method: 'POST',
      body: JSON.stringify({ bookingIds, paymentMethod, squarePaymentId }),
    }),

  // Reports
  getPerformanceReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/performance?${query}`);
  },
  getFinancialReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/financial?${query}`);
  },
  syncFinancialReport: (data: any) =>
    fetchApi<any>('/reports/financial/sync', { method: 'POST', body: JSON.stringify(data) }),
  reconcileReport: (data: any) =>
    fetchApi<any>('/reports/reconcile', { method: 'POST', body: JSON.stringify(data) }),
  getUtilizationReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/utilization?${query}`);
  },
  getComparisonReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/comparison?${query}`);
  },
  getRevenueReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/revenue?${query}`);
  },
  getWagesReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any[]>(`/reports/wages?${query}`);
  },
  getExpensesReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/expenses?${query}`);
  },
  getPayrollReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/payroll?${query}`);
  },
  getHistoricalOverview: () => fetchApi<any>('/reports/historical/overview'),
  importHistoricalData: (dataDir = 'current-data') =>
    fetchApi<any>('/reports/historical/import', {
      method: 'POST',
      body: JSON.stringify({ dataDir }),
    }),
  getAdTrackingReport: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi<any>(`/reports/ad-tracking?${query}`);
  },

  // Square cache sync
  getSquareSyncStatus: () => fetchApi<any>('/square/cache/status'),
  startSquareSync: (fromDate: string) =>
    fetchApi<any>('/square/cache/sync', { method: 'POST', body: JSON.stringify({ fromDate }) }),
  exportSquareCache: () => fetchApi<any>('/square/cache/export'),

  // No create, update or delete helpers: expenses come from Xero only, and
  // the sync would overwrite any local change on its next run.

  // Xero expense categorisation rules + sync — see apps/backend/src/expenses-sync.ts
  listExpenseRules: () =>
    fetchApi<
      Array<{
        xero_account_code: string;
        xero_account_name: string;
        category: string;
        period_type: 'one_off' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual';
        location_assignment: 'this_only' | 'split_50_50';
        location_id: number | null;
        updated_at: string;
      }>
    >('/expenses/rules'),
  upsertExpenseRule: (data: {
    xero_account_code: string;
    xero_account_name: string;
    category: string;
    period_type: 'one_off' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual';
    location_assignment: 'this_only' | 'split_50_50';
    location_id: number | null;
  }) => fetchApi<any>('/expenses/rules', { method: 'POST', body: JSON.stringify(data) }),
  deleteExpenseRule: (xeroAccountCode: string) =>
    fetchApi<any>(`/expenses/rules/${encodeURIComponent(xeroAccountCode)}`, {
      method: 'DELETE',
    }),
  listXeroExpenseAccounts: (tenantId: string) =>
    fetchApi<Array<{ accountId: string; code: string; name: string; type: string }>>(
      `/xero/expense-accounts?tenantId=${encodeURIComponent(tenantId)}`,
    ),
  syncExpensesFromXero: (data: { tenantId: string; since?: string | null }) =>
    fetchApi<{
      pulled_line_items: number;
      inserted: number;
      updated: number;
      unchanged: number;
      rules_auto_created: number;
      latest_bill_date: string | null;
    }>('/expenses/xero-sync', { method: 'POST', body: JSON.stringify(data) }),
  getExpenseSyncStatus: () =>
    fetchApi<
      Array<{
        tenant_id: string;
        tenant_name: string | null;
        last_trigger: 'manual' | 'scheduled' | null;
        last_triggered_by: string | null;
        last_run_at: string | null;
        last_status: 'success' | 'error' | null;
        last_error_message: string | null;
        pulled_line_items: number;
        inserted: number;
        updated: number;
        unchanged: number;
        rules_auto_created: number;
        latest_bill_date: string | null;
        duration_ms: number | null;
        latest_reconciled_expense_date: string | null;
        days_since_reconciled_expense: number | null;
      }>
    >('/expenses/xero-sync/status'),
  // Inventory
  getInventory: (includeInactive = false) =>
    fetchApi<any[]>(`/inventory${includeInactive ? '?includeInactive=true' : ''}`),
  createInventory: (data: any) =>
    fetchApi<any>('/inventory', { method: 'POST', body: JSON.stringify(data) }),
  updateInventory: (id: number, data: any) =>
    fetchApi<any>(`/inventory/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteInventory: (id: number) => fetchApi<any>(`/inventory/${id}`, { method: 'DELETE' }),
  syncInventoryWithSquare: () => fetchApi<any>('/inventory/sync-square', { method: 'POST' }),

  // Supplies
  getSupplies: (includeInactive = false) =>
    fetchApi<any[]>(`/supplies${includeInactive ? '?includeInactive=true' : ''}`),
  createSupply: (data: any) =>
    fetchApi<any>('/supplies', { method: 'POST', body: JSON.stringify(data) }),
  updateSupply: (id: number, data: any) =>
    fetchApi<any>(`/supplies/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteSupply: (id: number) => fetchApi<any>(`/supplies/${id}`, { method: 'DELETE' }),

  // Supply Requests
  getSupplyRequests: (params?: Record<string, string>) => {
    const query = params
      ? new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return fetchApi<{ requests: any[]; total: number }>(
      `/supply-requests${query ? `?${query}` : ''}`,
    );
  },
  createSupplyRequest: (data: any) =>
    fetchApi<any>('/supply-requests', { method: 'POST', body: JSON.stringify(data) }),
  fulfillSupplyRequest: (id: number) =>
    fetchApi<any>(`/supply-requests/${id}/fulfill`, { method: 'POST', body: JSON.stringify({}) }),

  // Customer Notes
  /** No-shows, late cancels and moves per mobile, keyed by its last 9 digits. */
  getCustomerReliability: (phones: string[]) =>
    fetchApi<Record<string, CustomerReliability>>(
      `/customer-reliability?phones=${encodeURIComponent(phones.join(','))}`,
    ),
  getCustomerNotes: (params: { email?: string; phone?: string; name?: string }) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ''),
    ).toString();
    return fetchApi<{ customerId: number | null; notes: string | null }>(
      `/customer-notes${query ? `?${query}` : ''}`,
    );
  },
  updateCustomerNotes: (data: { email?: string; phone?: string; name?: string; notes: string }) =>
    fetchApi<{ customerId: number; notes: string }>('/customer-notes', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Messaging
  getTemplates: (channel?: string) =>
    fetchApi<any[]>(`/messaging/templates${channel ? `?channel=${channel}` : ''}`),
  getTemplate: (id: number) => fetchApi<any>(`/messaging/templates/${id}`),
  createTemplate: (data: any) =>
    fetchApi<any>('/messaging/templates', { method: 'POST', body: JSON.stringify(data) }),
  updateTemplate: (id: number, data: any) =>
    fetchApi<any>(`/messaging/templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTemplate: (id: number) => fetchApi<any>(`/messaging/templates/${id}`, { method: 'DELETE' }),
  duplicateTemplate: (id: number) =>
    fetchApi<any>(`/messaging/templates/${id}/duplicate`, { method: 'POST' }),
  getContacts: (params: {
    search?: string;
    channel?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) =>
    fetchApi<any>(
      `/messaging/contacts?${new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v != null)
          .map(([k, v]) => [k, String(v)]),
      )}`,
    ),
  getContact: (id: number) => fetchApi<any>(`/messaging/contacts/${id}`),
  previewMessage: (data: {
    templateId: number;
    customerId?: number;
    variables?: Record<string, string>;
  }) => fetchApi<any>('/messaging/preview', { method: 'POST', body: JSON.stringify(data) }),
  sendMessage: (data: {
    templateId?: number;
    customerId?: number | null;
    contact?: {
      full_name?: string;
      first_name?: string;
      last_name?: string;
      email?: string;
      mobile?: string;
    };
    variables?: Record<string, string>;
    /** Pass `adhoc` instead of templateId to send a free-text message. */
    adhoc?: {
      channel: 'sms' | 'email';
      subject?: string;
      body: string;
    };
  }) => fetchApi<any>('/messaging/send', { method: 'POST', body: JSON.stringify(data) }),
  getSendLog: (params?: {
    templateId?: number;
    customerId?: number;
    channel?: string;
    status?: string;
    sentBy?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }) =>
    fetchApi<any>(
      `/messaging/send-log${
        params
          ? '?' +
            new URLSearchParams(
              Object.entries(params)
                .filter(([, v]) => v != null)
                .map(([k, v]) => [k, String(v)]),
            )
          : ''
      }`,
    ),
  getMessagingConfig: () => fetchApi<any>('/messaging/config'),
  getInboundMessages: (params?: { limit?: number; offset?: number; unreadOnly?: boolean }) =>
    fetchApi<any>(
      `/messaging/inbound${
        params
          ? '?' +
            new URLSearchParams(
              Object.entries(params)
                .filter(([, v]) => v != null)
                .map(([k, v]) => [k, String(v)]),
            )
          : ''
      }`,
    ),
  markInboundRead: (ids: number[]) =>
    fetchApi<any>('/messaging/inbound/read', {
      method: 'PATCH',
      body: JSON.stringify({ ids }),
    }),
  getRebookLinks: () => fetchApi<any[]>('/messaging/rebook-links'),
  getScheduledMessages: (params?: {
    status?: string;
    channel?: string;
    limit?: number;
    offset?: number;
  }) =>
    fetchApi<any>(
      `/messaging/schedule${
        params
          ? '?' +
            new URLSearchParams(
              Object.entries(params)
                .filter(([, v]) => v != null)
                .map(([k, v]) => [k, String(v)]),
            )
          : ''
      }`,
    ),
  scheduleMessage: (data: {
    templateId: number;
    customerId: number;
    variables?: Record<string, string>;
    scheduledFor: string;
  }) => fetchApi<any>('/messaging/schedule', { method: 'POST', body: JSON.stringify(data) }),
  cancelScheduledMessage: (id: number) =>
    fetchApi<any>(`/messaging/schedule/${id}`, { method: 'DELETE' }),
  getBarberUnwellPreview: (barberId: number, date: string) =>
    fetchApi<{
      templateId: number | null;
      templateName: string;
      templateBody: string | null;
      barberId: number;
      barberName: string;
      date: string;
      items: Array<{
        bookingId: string;
        customerName: string;
        mobile: string;
        email: string;
        appointmentDate: string;
        renderedBody: string;
        canSend: boolean;
        skipReason: string | null;
      }>;
    }>(`/messaging/barber-unwell/preview?barberId=${barberId}&date=${date}`),
  sendBarberUnwellBatch: (barberId: number, date: string) =>
    fetchApi<{ sent: number; failed: number; skipped: number; total: number }>(
      '/messaging/barber-unwell/send',
      { method: 'POST', body: JSON.stringify({ barberId, date }) },
    ),
  getBookingReminderPreview: () =>
    fetchApi<{
      templateId: number | null;
      templateName: string;
      templateBody: string | null;
      batches: Array<{
        label: string;
        runAt: string;
        batchSize: number;
        items: Array<{
          bookingId: string;
          customerName: string;
          mobile: string;
          barberName: string;
          appointmentDate: string;
          renderedBody: string;
        }>;
      }>;
    }>('/messaging/booking-reminder-preview'),
  getRebookingPreview: (limit?: number) =>
    fetchApi<{
      templateId: number | null;
      templateName: string;
      templateBody: string | null;
      nextRunAt: string;
      batchSize: number;
      items: Array<{
        customerId: number;
        customerName: string;
        mobile: string;
        lastAppointmentDate: string | null;
        renderedBody: string;
      }>;
    }>(`/messaging/rebooking-preview${limit ? `?limit=${limit}` : ''}`),

  createContact: (data: {
    full_name: string;
    email?: string | null;
    mobile?: string | null;
    phone?: string | null;
    sms_marketing_status?: string;
    email_marketing_status?: string;
  }) => fetchApi<any>('/messaging/contacts', { method: 'POST', body: JSON.stringify(data) }),

  updateContact: (
    id: number,
    data: {
      full_name?: string;
      email?: string | null;
      mobile?: string | null;
      phone?: string | null;
      sms_marketing_status?: string;
      email_marketing_status?: string;
    },
  ) => fetchApi<any>(`/messaging/contacts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Public Holidays
  getPublicHolidays: (params?: { year?: number; locationId?: number }) =>
    fetchApi<any[]>(
      `/public-holidays${
        params
          ? '?' +
            new URLSearchParams(
              Object.entries(params)
                .filter(([, v]) => v != null)
                .map(([k, v]) => [k, String(v)]),
            )
          : ''
      }`,
    ),
  createPublicHoliday: (data: { name: string; date: string; location_id?: number | null }) =>
    fetchApi<any>('/public-holidays', { method: 'POST', body: JSON.stringify(data) }),
  updatePublicHoliday: (id: number, data: any) =>
    fetchApi<any>(`/public-holidays/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deletePublicHoliday: (id: number) =>
    fetchApi<any>(`/public-holidays/${id}`, { method: 'DELETE' }),
  copyHolidaysFromYear: (sourceYear: number, targetYear: number) =>
    fetchApi<{ copied: number }>('/public-holidays/copy-year', {
      method: 'POST',
      body: JSON.stringify({ sourceYear, targetYear }),
    }),

  // Xero (payroll) — Slice 1: connection only.
  xeroStartConnect: () => fetchApi<{ authorizeUrl: string }>('/xero/connect', { method: 'POST' }),
  xeroStatus: () =>
    fetchApi<{
      connected: boolean;
      tenants: {
        tenantId: string;
        tenantName: string;
        tenantType: string | null;
        scopes: string;
        tokenExpiresAt: string;
        connectedByEmail: string | null;
        updatedAt: string;
      }[];
    }>('/xero/status'),
  xeroDisconnect: (tenantId: string) =>
    fetchApi<{ ok: true }>(`/xero/tenants/${tenantId}`, { method: 'DELETE' }),

  // Slice 2 — barber ↔ Xero employee mapping.
  xeroListEmployees: (tenantId: string) =>
    fetchApi<{
      employees: {
        employeeId: string;
        firstName: string;
        lastName: string;
        status: string | null;
        email: string | null;
        classification: string | null;
      }[];
      status: 'ok' | 'no-payroll' | 'reconsent' | 'error';
      error?: string;
    }>(`/xero/payroll/employees?tenantId=${encodeURIComponent(tenantId)}`),
  payrollBarberMappings: (tenantId: string) =>
    fetchApi<{
      mappings: {
        barberId: number;
        barberName: string;
        tenantId: string;
        xeroEmployeeId: string | null;
        xeroEmployeeName: string | null;
        mappedByEmail: string | null;
        updatedAt: string | null;
      }[];
    }>(`/payroll/barber-mappings?tenantId=${encodeURIComponent(tenantId)}`),
  payrollUpsertBarberMapping: (data: {
    barberId: number;
    tenantId: string;
    xeroEmployeeId: string;
    xeroEmployeeName: string;
  }) =>
    fetchApi<{ ok: true }>(`/payroll/barber-mappings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  payrollDeleteBarberMapping: (barberId: number, tenantId: string) =>
    fetchApi<{ ok: true }>(
      `/payroll/barber-mappings/${barberId}?tenantId=${encodeURIComponent(tenantId)}`,
      { method: 'DELETE' },
    ),
  payrollTimesheetPreview: (tenantId: string, startDate: string, endDate: string) =>
    fetchApi<{
      tenantId: string;
      startDate: string;
      endDate: string;
      rows: {
        barberId: number;
        barberName: string;
        xeroEmployeeId: string;
        xeroEmployeeName: string;
        days: { date: string; hours: number; isSaturday: boolean; isSunday: boolean }[];
        totalHours: number;
        ordinaryHours: number;
        saturdayHours: number;
        sundayHours: number;
      }[];
    }>(
      `/payroll/timesheet/preview?tenantId=${encodeURIComponent(tenantId)}&startDate=${startDate}&endDate=${endDate}`,
    ),
  payrollTimesheetPush: (data: {
    tenantId: string;
    barberId: number;
    startDate: string;
    endDate: string;
  }) =>
    fetchApi<{
      id: number;
      tenantId: string;
      barberId: number;
      xeroEmployeeId: string;
      startDate: string;
      endDate: string;
      xeroTimesheetId: string;
      status: string;
      totalHours: number;
    }>(`/payroll/timesheet/push`, { method: 'POST', body: JSON.stringify(data) }),
  payrollTimesheetsPushed: (tenantId: string, startDate: string, endDate: string) =>
    fetchApi<{
      pushed: {
        barberId: number;
        xeroTimesheetId: string;
        status: string;
        totalHours: number;
        pushedByEmail: string | null;
        updatedAt: string;
        startDate: string;
        endDate: string;
      }[];
    }>(
      `/payroll/timesheet/pushed?tenantId=${encodeURIComponent(tenantId)}&startDate=${startDate}&endDate=${endDate}`,
    ),
  payrollPayCalendars: (tenantId: string) =>
    fetchApi<{
      calendars: {
        payrollCalendarId: string;
        name: string;
        calendarType: string;
        startDate: string;
        paymentDate: string | null;
      }[];
    }>(`/payroll/pay-calendars?tenantId=${encodeURIComponent(tenantId)}`),
  payrollCreatePayRun: (data: {
    tenantId: string;
    payrollCalendarId: string;
    periodEndDate: string;
  }) =>
    fetchApi<{
      id: number;
      tenantId: string;
      payrollCalendarId: string;
      periodStartDate: string;
      periodEndDate: string;
      paymentDate: string | null;
      xeroPayRunId: string;
      status: string;
    }>(`/payroll/pay-runs`, { method: 'POST', body: JSON.stringify(data) }),
  payrollListPayRuns: (tenantId: string) =>
    fetchApi<{
      payRuns: {
        id: number;
        payrollCalendarId: string;
        periodStartDate: string;
        periodEndDate: string;
        paymentDate: string | null;
        xeroPayRunId: string;
        status: string;
        createdByEmail: string | null;
        updatedAt: string;
      }[];
    }>(`/payroll/pay-runs?tenantId=${encodeURIComponent(tenantId)}`),
  payrollLeaveTypes: () => fetchApi<{ leaveTypes: string[] }>(`/payroll/leave/types`),
  payrollListLeave: (filter?: { barberId?: number; fromDate?: string }) => {
    const qs = new URLSearchParams();
    if (filter?.barberId) qs.set('barberId', String(filter.barberId));
    if (filter?.fromDate) qs.set('fromDate', filter.fromDate);
    const suffix = qs.toString();
    return fetchApi<{
      leave: {
        id: number;
        barberId: number;
        barberName: string;
        leaveType: string;
        startDate: string;
        endDate: string;
        description: string | null;
        createdByEmail: string | null;
        createdAt: string;
      }[];
    }>(`/payroll/leave${suffix ? `?${suffix}` : ''}`);
  },
  payrollCreateLeave: (data: {
    barberId: number;
    leaveType: string;
    startDate: string;
    endDate: string;
    description?: string | null;
  }) =>
    fetchApi<{
      id: number;
      barberId: number;
      barberName: string;
      leaveType: string;
      startDate: string;
      endDate: string;
      description: string | null;
      createdByEmail: string | null;
      createdAt: string;
    }>(`/payroll/leave`, { method: 'POST', body: JSON.stringify(data) }),
  payrollDeleteLeave: (id: number) =>
    fetchApi<{ ok: true }>(`/payroll/leave/${id}`, { method: 'DELETE' }),
  payrollWeekly: (dateInWeek: string, tenantId?: string) => {
    const qs = new URLSearchParams({ dateInWeek });
    if (tenantId) qs.set('tenantId', tenantId);
    return fetchApi<{
      startDate: string;
      endDate: string;
      paymentDate: string;
      rows: {
        barberId: number;
        barberName: string;
        employmentType: 'FT' | 'PT' | 'CASUAL';
        xeroEmployeeId: string | null;
        xeroEmployeeName: string | null;
        weekdayHours: number;
        saturdayHours: number;
        sundayHours: number;
        // Mon..Sun, exactly 7 entries — `hours: 0` for non-working days.
        days: { date: string; dow: number; hours: number }[];
        weekdayRate: number;
        saturdayRate: number;
        ordinaryEarnings: number;
        tips: number;
        serviceRevenue: number;
        bonusRate: number;
        bonusAmount: number;
        grossPay: number;
        superAmount: number;
      }[];
      totals: {
        weekdayHours: number;
        saturdayHours: number;
        sundayHours: number;
        ordinaryEarnings: number;
        tips: number;
        bonusAmount: number;
        grossPay: number;
        superAmount: number;
      };
      existingPayRun: { xeroPayRunId: string; status: string } | null;
    }>(`/payroll/weekly?${qs.toString()}`);
  },
  payrollRunWeekly: (data: { dateInWeek: string; tenantId: string; paymentDate?: string }) =>
    fetchApi<{
      startDate: string;
      endDate: string;
      paymentDate: string;
      steps: { step: string; ok: boolean; error?: string }[];
      payRunXeroId: string | null;
      payRunStatus: string | null;
      totalGross: number;
      totalSuper: number;
    }>(`/payroll/weekly/run`, { method: 'POST', body: JSON.stringify(data) }),
  payrollGetPayRun: (id: number) =>
    fetchApi<{
      xeroPayRunId: string;
      payrollCalendarId: string;
      periodStartDate: string;
      periodEndDate: string;
      paymentDate: string | null;
      status: string;
      totals: { wages: number; tax: number; super: number; netPay: number };
      stpStatus: 'not-yet-filed' | 'filed-by-xero' | 'unknown';
      payslips: {
        payslipId: string;
        employeeId: string;
        employeeName: string;
        wages: number;
        tax: number;
        super: number;
        reimbursements: number;
        deductions: number;
        netPay: number;
      }[];
    }>(`/payroll/pay-runs/${id}`),
};

export default api;
